import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  increment,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { supabase } from "../supabase";
import Layout from "../components/Layout";
import { syncLedgerOrderAmount, getISTDateString } from "../services/cashierService";
import { 
  normalizeAndMergeItems, 
  splitOrderToOutlets, 
  calculateLineTotals 
} from "../utils/orderUtils";
import { generateTripSheetPdf } from "../utils/pdfTripSheet";
import { generateChallanPdf } from "../utils/pdfChallan";
import { buildHsnSummary } from "../utils/hsnSummary";

function EditApprovedOrder() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState({}); // { SKU: qty }
  const [skuMaster, setSkuMaster] = useState({});
  const [skuListOrdered, setSkuListOrdered] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [partyData, setPartyData] = useState(null);
  const [company, setCompany] = useState(null);
  const [savingStep, setSavingStep] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // 1. Fetch Order
        const orderSnap = await getDoc(doc(db, "orders", orderId));
        if (!orderSnap.exists()) {
          alert("Order not found");
          navigate("/cancel-invoices");
          return;
        }
        const oData = orderSnap.data();
        if (oData.ledgerSynced) {
          alert("🚫 Locked: This order is already synced via Ledger Approval.");
          navigate("/cancel-invoices");
          return;
        }
        setOrder(oData);
        setItems(oData.items || {});

        // 2. Fetch SKU Master
        const skuSnap = await getDocs(collection(db, "skuMaster"));
        const skuMap = {};
        const skuArr = [];
        skuSnap.forEach(d => {
          const data = d.data();
          if (!data.sku) return;
          const skuKey = data.sku.trim().toUpperCase();
          skuMap[skuKey] = data;
          skuArr.push({ sku: skuKey, sortorder: Number(data.sortorder ?? 9999) });
        });
        skuArr.sort((a, b) => a.sortorder - b.sortorder);
        setSkuMaster(skuMap);
        setSkuListOrdered(skuArr.map(x => x.sku));

        // 3. Fetch Outlets & Party Master Data
        if (oData.partyId) {
          const qOutlets = query(
            collection(db, "outletMaster"), 
            where("partyId", "==", oData.partyId), 
            where("status", "==", "active")
          );
          const snapOutlets = await getDocs(qOutlets);
          setOutlets(snapOutlets.docs.map(d => ({ id: d.id, ...d.data() })));

          const pSnap = await getDoc(doc(db, "partyMaster", oData.partyId));
          if (pSnap.exists()) setPartyData(pSnap.data());
        }

        // 4. Fetch Company
        const cSnap = await getDoc(doc(db, "companyInfo", "default"));
        if (cSnap.exists()) setCompany(cSnap.data());

        setLoading(false);
      } catch (err) {
        console.error(err);
        alert("Failed to load order data");
      }
    };
    fetchData();
  }, [orderId, navigate]);

  const updateQty = (sku, val) => {
    setItems(prev => ({ ...prev, [sku]: Number(val) }));
  };

  const totals = useMemo(() => {
    let tQty = 0, tBasic = 0, tGST = 0, tCess = 0, tAddl = 0, tRebate = 0, tGrand = 0, tEway = 0;
    Object.entries(items).forEach(([sku, qty]) => {
      if (qty <= 0) return;
      const m = skuMaster[sku];
      if (!m) return;
      const line = calculateLineTotals(m, qty);
      tQty += line.qty;
      tBasic += line.basicAmount;
      tGST += line.gstAmount;
      tCess += line.cessAmount;
      tAddl += line.addlCessAmount;
      tRebate += line.rebateAmount;
      tGrand += line.lineTotal;
      tEway += line.ewayLineTotal;
    });
    return { tQty, tBasic, tGST, tCess, tAddl, tRebate, tGrand, tEway };
  }, [items, skuMaster]);

  const uploadPdf = async (bucket, filePath, blob) => {
    const { error } = await supabase.storage.from(bucket).upload(filePath, blob, {
      contentType: "application/pdf",
      upsert: true,
    });
    if (error) throw error;
    const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
    return data.publicUrl;
  };

  const saveEdit = async () => {
    if (saving) return;
    const ddmmyy = getISTDateString().split("-").reverse().join("").slice(0, 4) + getISTDateString().slice(2, 4); 
    // Wait, ddmmyy Pin logic: dd + mm + yy
    const istNow = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const realPin = String(istNow.getDate()).padStart(2, '0') + 
                    String(istNow.getMonth() + 1).padStart(2, '0') + 
                    String(istNow.getFullYear()).slice(-2);
    
    const pwd = prompt("Enter Dynamic PIN (ddmmyy) to Authorize Edit:");
    if (pwd !== realPin) {
      alert("Incorrect PIN");
      return;
    }

    try {
      setSaving(true);
      setSavingStep("Reversing old order impact...");

      const batch = writeBatch(db);

      // 1. REVERSE OLD IMPACT
      // 1a. Old Order Lines -> Restore Stock
      const oldLinesSnap = await getDocs(query(collection(db, "orderLines"), where("orderId", "==", orderId)));
      oldLinesSnap.forEach(d => {
        const data = d.data();
        const stockDocId = `${order.approvedDateOnly}_${data.sku}`;
        batch.set(doc(db, "daily_stock", stockDocId), {
          invoiceQty: increment(-Number(data.qty))
        }, { merge: true });
        batch.delete(d.ref);
      });

      // 1b. Old Outlet Orders -> Refund Annual Limits
      const oldOutletSnap = await getDocs(query(collection(db, "outletOrders"), where("rootOrderId", "==", orderId)));
      const outletRefundMap = {};
      oldOutletSnap.forEach(d => {
        const data = d.data();
        outletRefundMap[data.outletId] = (outletRefundMap[data.outletId] || 0) + Number(data.totalAmount);
        batch.delete(d.ref);
      });
      for (const [oid, amt] of Object.entries(outletRefundMap)) {
        batch.update(doc(db, "outletMaster", oid), { annualUsed: increment(-amt) });
      }

      setSavingStep("Calculating new distribution...");

      // 2. APPLY NEW IMPACT
      const normalized = normalizeAndMergeItems(items);
      const limitTrackers = outlets.map(o => {
        const remaining = Math.max(0, (Number(o.annualLimit) || 0) - (Number(o.annualUsed || 0) - (outletRefundMap[o.id] || 0)));
        const effective = Math.min(Number(o.dailyInvoiceLimit || 0), remaining);
        return { id: o.id, remainingLimit: effective };
      });

      const skuSplits = {};
      const newOrderLines = [];
      const newOutletOrders = [];

      Object.entries(normalized).forEach(([sku, qty]) => {
        const m = skuMaster[sku];
        const line = calculateLineTotals(m, qty);
        
        // Split across outlets
        const distribution = splitOrderToOutlets(qty, m.minQty || 1, limitTrackers, line.totalAmount / qty);
        skuSplits[sku] = distribution;

        // Add to OrderLines (Individual Docs Collection)
        newOrderLines.push({
          ...line,
          clientId: order.clientId || "",
          orderId,
          displayOrderId: order.displayOrderId,
          approvedAt: new Date(),
          approvedDateOnly: order.approvedDateOnly,
          partyId: order.partyId,
          partyName: order.partyName,
          partyPhone: partyData?.phone || "",
          partyType: partyData?.partytype || "",
          creditLimit: partyData?.creditLimit || 0,
          dmsName: partyData?.dms_name || "",
          easyCigName: partyData?.easycig_name || "",
          gstno: partyData?.gstno || "",
          route: partyData?.route || "",
          salesman: partyData?.salesman || "",
          address: order.address || partyData?.address || "",
          payment_mode: order.payment_mode || "CASH",
          source: order.source || "web",
          vehicleNo: order.vehicleNo || "",
          sku,
          skuName: m.Name,
          description: m.description || "",
          msku: m.msku || "",
          segment: m.segment || "",
          brand: m.Brand || "",
          itemCode: m.ItemCode || "",
          status: "APPROVED"
        });
      });

      // 🚩 REPLICATE LEGACY ARRAY FOR ROOT ORDER SNAPSHOT
      const newLegacyOrderLines = newOrderLines.map((line, idx) => ({
        lineNo: idx + 1,
        sku: line.sku || "",
        itemCode: line.itemCode || "",
        skuName: line.skuName || "",
        description: line.description || "",
        brand: line.brand || "",
        msku: line.msku || "",
        segment: line.segment || "",
        qty: line.qty || 0,
        rate: line.rate || 0,
        basicAmount: line.basicAmount || 0,
        gstAmount: line.gstAmount || 0,
        cessAmount: line.cessAmount || 0,
        addlCessAmount: line.addlCessAmount || 0,
        rebatePerUnit: line.rebatePerUnit || 0,
        addlCessPerUnit: line.addlCessPerUnit || 0,
        lineTotal: line.lineTotal || 0, // Collection uses lineTotal
        ewayLineTotal: line.ewayLineTotal || 0, // Collection uses ewayLineTotal
        totalAmount: line.lineTotal || 0, // Root order snapshot uses totalAmount
        ewayAmount: line.ewayLineTotal || 0, // Root order snapshot uses ewayAmount
        hsn: line.hsn || ""
      }));

      setSavingStep("Generating new documents...");

      // PDFs
      const hsnSummary = buildHsnSummary(normalized, skuMaster);
      const tripPdf = generateTripSheetPdf(
        order.displayOrderId, 
        { ...order, items: normalized }, 
        normalized, 
        skuMaster, 
        { 
          totalQty: totals.tQty, 
          grandTotal: totals.tGrand 
        }, 
        "Updated Trip Sheet"
      );
      const challanPdf = generateChallanPdf(
        order.displayOrderId, 
        { ...order, items: normalized }, 
        hsnSummary, 
        { 
          totalQty: totals.tQty,
          totalBasic: totals.tBasic,
          totalGST: totals.tGST,
          totalCESS: totals.tCess,
          totalAddlCess: totals.tAddl,
          grandTotal: totals.tEway // Eway amount (no rebate)
        }, 
        company
      );
      
      const tripUrl = await uploadPdf("tripsheets", `tripsheets/TripSheet-${order.displayOrderId}.pdf`, tripPdf.output("blob"));
      const challanUrl = await uploadPdf("challans", `Challan-${order.displayOrderId}.pdf`, challanPdf.output("blob"));

      // 3. QUEUE NEW OPS
      newOrderLines.forEach(line => {
        batch.set(doc(collection(db, "orderLines")), line);
        // Deduct new stock
        const stockDocId = `${order.approvedDateOnly}_${line.sku}`;
        batch.set(doc(db, "daily_stock", stockDocId), {
          clientId: order.clientId || "",
          invoiceQty: increment(line.qty),
          date: order.approvedDateOnly,
          sku: line.sku,
          updatedAt: serverTimestamp()
        }, { merge: true });
      });

      // Outlet Orders loop
      for (let i = 0; i < outlets.length; i++) {
        const outlet = outlets[i];
        let outletTotal = 0;
        Object.keys(skuSplits).forEach(sku => {
          const q = skuSplits[sku][i];
          if (q > 0) {
            const m = skuMaster[sku];
            const line = calculateLineTotals(m, q);
            outletTotal += line.lineTotal;
            batch.set(doc(collection(db, "outletOrders")), {
              ...line,
              clientId: order.clientId || "",
              rootOrderId: orderId,
              rootDisplayOrderId: order.displayOrderId,
              displayOrderId: `${order.displayOrderId}-${i + 1}`,
              outletId: outlet.id,
              outletName: outlet.outletName,
              status: "APPROVED",
              approvedDateOnly: order.approvedDateOnly,
              partyId: order.partyId,
              partyName: order.partyName,
              partyPhone: partyData?.phone || "",
              partyType: partyData?.partytype || "",
              payment_mode: order.payment_mode || "CASH",
              dmsName: partyData?.dms_name || "",
              easyCigName: partyData?.easycig_name || "",
              gstno: partyData?.gstno || "",
              route: partyData?.route || "",
              salesman: partyData?.salesman || "",
              address: order.address || partyData?.address || "",
              vehicleNo: order.vehicleNo || "",
              tripSheetPdfUrl: tripUrl || "",
              challanPdfUrl: challanUrl || ""
            });
          }
        });
        if (outletTotal > 0) {
          batch.update(doc(db, "outletMaster", outlet.id), { annualUsed: increment(outletTotal) });
        }
      }

      batch.update(doc(db, "orders", orderId), {
        items: normalized,
        orderLines: newLegacyOrderLines, // ✅ SYNCED SNAPSHOT
        totalQty: totals.tQty,
        totalAmount: totals.tGrand,
        ewayAmount: totals.tEway,        // ✅ SYNCED EWAY TOTAL
        tripSheetPdfUrl: tripUrl || "",
        challanPdfUrl: challanUrl || "",
        updatedAt: serverTimestamp()
      });

      setSavingStep("Finalizing...");
      await batch.commit();

      setSavingStep("Syncing ledger...");
      await syncLedgerOrderAmount(order.partyId, order.approvedDateOnly);

      alert("Invoice updated successfully!");
      navigate("/cancel-invoices");
    } catch (err) {
      console.error(err);
      alert("Failed to save edits: " + err.message);
    } finally {
      setSaving(false);
      setSavingStep("");
    }
  };

  if (loading) return <Layout><div style={{ padding: 20 }}>Loading Order Data...</div></Layout>;

  return (
    <Layout>
      <div style={{ padding: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2>✏️ Edit Approved Invoice: {order.displayOrderId}</h2>
          <div style={{ display: "flex", gap: 10 }}>
            <button 
              onClick={() => navigate("/cancel-invoices")}
              style={{ padding: "10px 20px", background: "#eee", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer" }}
            >
              Back
            </button>
            <button 
              onClick={saveEdit}
              disabled={saving}
              style={{ padding: "10px 20px", background: "#4caf50", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: "bold" }}
            >
              {saving ? savingStep : "💾 Save Changes"}
            </button>
          </div>
        </div>

        <div style={{ background: "#fff", padding: 20, borderRadius: 8, boxShadow: "0 2px 4px rgba(0,0,0,0.05)", marginBottom: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            <div><strong>Party:</strong> {order.partyName} ({order.partyId})</div>
            <div><strong>Billing Date:</strong> {order.approvedDateOnly}</div>
            <div><strong>Vehicle No:</strong> {order.vehicleNo || "-"}</div>
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
          <thead>
            <tr style={{ background: "#f8f9fa", textAlign: "left" }}>
              <th style={{ padding: 12, border: "1px solid #ddd" }}>SKU</th>
              <th style={{ padding: 12, border: "1px solid #ddd" }}>Rate</th>
              <th style={{ padding: 12, border: "1px solid #ddd" }}>Current Qty</th>
              <th style={{ padding: 12, border: "1px solid #ddd" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {skuListOrdered.map(sku => {
              const qty = items[sku] || 0;
              const m = skuMaster[sku];
              if (qty === 0 && !items.hasOwnProperty(sku)) return null; // Show existing items by default
              
              const line = calculateLineTotals(m, qty);

              return (
                <tr key={sku}>
                  <td style={{ padding: 12, border: "1px solid #ddd" }}>
                    <strong>{sku}</strong>
                    <div style={{ fontSize: 11, color: "#666" }}>{m.Name}</div>
                  </td>
                  <td style={{ padding: 12, border: "1px solid #ddd" }}>₹{m.Basic_Eway}</td>
                  <td style={{ padding: 12, border: "1px solid #ddd" }}>
                    <input 
                      type="number" 
                      value={qty} 
                      onChange={(e) => updateQty(sku, e.target.value)}
                      style={{ width: 80, padding: "5px" }}
                    />
                  </td>
                  <td style={{ padding: 12, border: "1px solid #ddd" }}>₹{line.lineTotal.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals Summary */}
        <div style={{ marginTop: 20, textAlign: "right", background: "#fff", padding: 20, borderRadius: 8 }}>
            <div style={{ fontSize: 18, marginBottom: 5 }}>Total Qty: <strong>{totals.tQty.toFixed(2)}</strong></div>
            <div style={{ fontSize: 24, color: "#2E7D32" }}>Grand Total: <strong>₹{totals.tGrand.toFixed(2)}</strong></div>
        </div>

        {/* Helper to add other SKUs */}
        <div style={{ marginTop: 20 }}>
          <label>Add SKU (from Master): </label>
          <select onChange={(e) => updateQty(e.target.value, 0)} value="">
            <option value="">-- Choose SKU to add --</option>
            {skuListOrdered.filter(s => !items.hasOwnProperty(s)).map(s => (
                <option key={s} value={s}>{s} - {skuMaster[s].Name}</option>
            ))}
          </select>
        </div>
        
        {/* --- GLOBAL PROCESSING OVERLAY --- */}
        {saving && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}>
            <div style={{
              background: "#fff",
              padding: "40px",
              borderRadius: "20px",
              textAlign: "center",
              width: "400px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)"
            }}>
              <div style={{ fontSize: "50px", marginBottom: "20px" }}>⚡</div>
              <h3 style={{ margin: "0 0 10px 0", fontSize: "20px", fontWeight: 800 }}>Applying Changes...</h3>
              <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "20px" }}>{savingStep}</p>
              <div style={{
                height: "6px",
                width: "100%",
                background: "#f1f5f9",
                borderRadius: "10px",
                overflow: "hidden"
              }}>
                <div style={{
                  height: "100%",
                  width: "100%",
                  background: "linear-gradient(90deg, #4f46e5, #9333ea)",
                  animation: "loadingBar 2s infinite ease-in-out"
                }}></div>
              </div>
              <style>{`
                @keyframes loadingBar {
                  0% { transform: translateX(-100%); }
                  100% { transform: translateX(100%); }
                }
              `}</style>
              <p style={{ marginTop: "30px", fontSize: "11px", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                System is updating collections. Do not refresh.
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default EditApprovedOrder;
